import { useMemo, useEffect, useState, useRef } from 'react';
import { ResourceType } from 'libs.library_manager';
import { AssetTypes } from 'libs.localization.common';
import { useLocalization } from 'libs.localization.react';
import { useLibraryDetails } from './library_details.hook';
import { useActivityDetails } from './use_activity_details.hook';
export var ActivityOrigin;
(function (ActivityOrigin) {
    ActivityOrigin["ORIGINAL"] = "original";
    ActivityOrigin["CLONED"] = "cloned";
    ActivityOrigin["VERSIONED"] = "versioned";
})(ActivityOrigin || (ActivityOrigin = {}));
/**
 * Hook to determine activity source status and fetch source activity details
 * @param activityId - The activity ID to check
 * @returns Object containing source activity status information
 */
export const useActivitySourceStatus = (activityId) => {
    // Get current activity details using useLibraryDetails
    const { data: activityResponse, isLoading: isLoadingActivity } = useLibraryDetails(activityId, ResourceType.ACTIVITY);
    // Extract current activity data from the correct path
    const activity = activityResponse?.data?.data?.data;
    const currentVersion = parseInt(activityResponse?.data?.data?.version || '1', 10);
    const currentKey = activityResponse?.data?.data?.key;
    // Get source_id from activity metadata (original clone detection logic)
    const sourceId = activityResponse?.data?.data?.metadata?.source_id;
    const sourceLink = sourceId ? `/library/activities/${sourceId}` : undefined;
    // Determine if this is a cloned activity (source_id exists and version is 1) or versioned activity (version > 1)
    const isCloned = sourceId && currentVersion === 1;
    // For cloned activities, use useLibraryDetails with sourceId
    const { data: clonedSourceResponse, isLoading: isLoadingClonedSource } = useLibraryDetails(isCloned ? sourceId : undefined, ResourceType.ACTIVITY);
    // For versioned activities, calculate source activity parameters
    const sourceActivityParams = useMemo(() => {
        // Only fetch source activity if we have a sourceId and currentKey, and it's not a clone
        if (!sourceId || !currentKey || isCloned) {
            return undefined;
        }
        // For versioned activities: version=currentVersion-1 and active=false
        const sourceVersion = Math.max(1, currentVersion - 1);
        return {
            key: currentKey,
            version: sourceVersion,
            active: false,
        };
    }, [sourceId, currentKey, currentVersion, isCloned]);
    // Fetch source activity using the calculated parameters for versioned activities
    const { data: versionedSourceResponse, isLoading: isLoadingVersionedSource } = useActivityDetails(sourceActivityParams);
    // Determine which response to use based on activity type
    const sourceActivityResponse = isCloned ? clonedSourceResponse : versionedSourceResponse;
    const isLoadingSourceActivity = isCloned ? isLoadingClonedSource : isLoadingVersionedSource;
    // Extract source activity data - handle both response formats
    const sourceActivity = isCloned
        ? sourceActivityResponse?.data?.data?.data
        : sourceActivityResponse?.data?.[0]?.data;
    const sourceActivityName = useMemo(() => sourceActivity?.short_name, [sourceActivity?.short_name]);
    const sourceActivityVersion = useMemo(() => {
        if (isCloned) {
            return sourceActivityResponse?.data?.data?.version;
        }
        return sourceActivityResponse?.data?.[0]?.version;
    }, [isCloned, sourceActivityResponse?.data?.data?.version, sourceActivityResponse?.data?.[0]?.version]);
    // Handle localization data asynchronously - only when sourceActivity is available
    const sourceAsset = useMemo(() => {
        return sourceActivity?.identifier
            ? {
                type: AssetTypes.ACTIVITY,
                id: sourceActivity.identifier,
                version: '1.0',
            }
            : undefined;
    }, [sourceActivity?.identifier]);
    const { localization: sourceLocalization } = useLocalization(sourceAsset);
    // State for supported locales
    const [supportedLocales, setSupportedLocales] = useState(undefined);
    const [isLoadingSupportedLocales, setIsLoadingSupportedLocales] = useState(false);
    const previousSourceAssetId = useRef(undefined);
    // Fetch supported locales asynchronously
    useEffect(() => {
        const currentSourceAssetId = sourceAsset?.id;
        // Only fetch if the source asset ID has actually changed
        if (currentSourceAssetId === previousSourceAssetId.current) {
            return;
        }
        previousSourceAssetId.current = currentSourceAssetId;
        const fetchSupportedLocales = async () => {
            if (!sourceLocalization) {
                setSupportedLocales(undefined);
                setIsLoadingSupportedLocales(false);
                return;
            }
            setIsLoadingSupportedLocales(true);
            try {
                const sourceDbEntry = await sourceLocalization.getDatabaseEntry();
                const allLocales = sourceDbEntry?.supported_locales ? Object.keys(sourceDbEntry.supported_locales) : [];
                // Filter out en-US (base locale) from supported locales
                const locales = allLocales.filter((locale) => locale !== 'en-US');
                setSupportedLocales(locales);
            }
            catch (error) {
                console.error('Failed to fetch supported locales:', error);
                setSupportedLocales(undefined);
            }
            finally {
                setIsLoadingSupportedLocales(false);
            }
        };
        fetchSupportedLocales();
    }, [sourceAsset?.id, sourceLocalization]);
    const activityOrigin = useMemo(() => {
        if (!sourceId) {
            return ActivityOrigin.ORIGINAL;
        }
        // Check if this is a clone (version 1) or a versioned activity (version > 1)
        return currentVersion === 1 ? ActivityOrigin.CLONED : ActivityOrigin.VERSIONED;
    }, [sourceId, currentVersion]);
    // Memoize early return objects to prevent unnecessary re-renders
    const emptyState = useMemo(() => ({
        sourceId: undefined,
        sourceLink: undefined,
        sourceActivityName: undefined,
        sourceActivity: undefined,
        isLoadingSourceActivity: false,
        sourceActivityVersion: undefined,
        activityOrigin: ActivityOrigin.ORIGINAL,
        isSourceTranslationsAvailable: false,
        isLoadingSupportedLocales: false,
        currentActivity: undefined,
        sourceLocalization: undefined,
    }), []);
    // Memoize the main return object
    const mainReturnObject = useMemo(() => ({
        sourceId,
        sourceLink,
        sourceActivityName,
        sourceActivity,
        isLoadingSourceActivity,
        sourceActivityVersion,
        activityOrigin,
        isSourceTranslationsAvailable: Array.isArray(supportedLocales) && supportedLocales.length > 0,
        isLoadingSupportedLocales,
        currentActivity: activity,
        sourceLocalization,
    }), [
        sourceId,
        sourceLink,
        sourceActivityName,
        sourceActivity,
        isLoadingSourceActivity,
        sourceActivityVersion,
        activityOrigin,
        supportedLocales,
        isLoadingSupportedLocales,
        activity,
        sourceLocalization,
    ]);
    // Return early loading state if activity is still loading
    if (isLoadingActivity || !activityId) {
        return emptyState;
    }
    // Return early if no activity found
    if (!activity) {
        return emptyState;
    }
    return mainReturnObject;
};
